import React, { useState, useEffect } from 'react';
import AdminNav from '../components/AdminNav';
import { getVolunteers, getAssignments, getLocations, getShiftTimes, updateVolunteerCheckIn } from '../services/firebase';
import './Admin.css';

const DAYS = ['Thu-April-9', 'Fri-April-10', 'Sat-April-11', 'Sun-April-12'];

const DEFAULT_LOCATIONS = [
  'Washington Road',
  'Surrey Center',
  'Downtown Augusta',
  'James Brown Blvd',
  'Riverwalk'
];

function CheckInInterface({ onLogout }) {
  const [volunteers, setVolunteers] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [checkInStatus, setCheckInStatus] = useState({});
  const [locations, setLocations] = useState([]);
  const [shiftTimes, setShiftTimes] = useState([]);
  const [selectedDay, setSelectedDay] = useState(DAYS[0]);
  const [selectedShift, setSelectedShift] = useState('');
  const [loading, setLoading] = useState(true);
  const [expandedLocations, setExpandedLocations] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const [volData, assignmentData, locationData, shiftTimeData] = await Promise.all([
      getVolunteers(),
      getAssignments(),
      getLocations(),
      getShiftTimes(),
    ]);
    setVolunteers(volData);

    const activeLocations = (locationData.length > 0
      ? locationData.filter(l => l.active).map(l => l.name)
      : DEFAULT_LOCATIONS
    ).sort((a, b) => a.localeCompare(b));
    setLocations(activeLocations);

    setShiftTimes(shiftTimeData);
    if (shiftTimeData.length > 0) setSelectedShift(shiftTimeData[0].name);
    
    const assignmentMap = {};
    assignmentData.forEach(a => {
      const key = `${a.day}_${a.location}_${a.shiftTime}`;
      assignmentMap[key] = a.volunteers || [];
    });
    setAssignments(assignmentMap);
    
    // Load check-in status from volunteers
    const statusMap = {};
    volData.forEach(v => {
      Object.keys(v).forEach(key => {
        if (key.startsWith('checkIn_')) {
          statusMap[`${v.id}_${key.replace('checkIn_', '')}`] = v[key];
        }
      });
    });
    setCheckInStatus(statusMap);
    
    setLoading(false);
  };

  const handleCheckIn = async (volunteerId, shiftId, status) => {
    const key = `${volunteerId}_${shiftId}`;
    setCheckInStatus({
      ...checkInStatus,
      [key]: status
    });
    
    await updateVolunteerCheckIn(volunteerId, shiftId, status);
  };

  const getVolunteerInfo = (volunteerId) => {
    return volunteers.find(v => v.id === volunteerId);
  };

  const toggleLocation = (location) => {
    setExpandedLocations({
      ...expandedLocations,
      [location]: !expandedLocations[location]
    });
  };

  const getShiftStats = () => {
    let total = 0;
    let checkedIn = 0;
    let noShows = 0;

    locations.forEach(location => {
      const key = `${selectedDay}_${location}_${selectedShift}`;
      const assigned = assignments[key] || [];
      assigned.filter(Boolean).forEach(volunteerId => {
        total++;
        const shiftId = key.replace(/\s+/g, '_');
        const statusKey = `${volunteerId}_${shiftId}`;
        const status = checkInStatus[statusKey];
        if (status === 'checked-in') checkedIn++;
        if (status === 'no-show') noShows++;
      });
    });

    return { total, checkedIn, noShows, pending: total - checkedIn - noShows };
  };

  if (loading) {
    return <div className="admin-container"><div className="loading">Loading...</div></div>;
  }

  const stats = getShiftStats();

  return (
    <div className="admin-container">
      <AdminNav onLogout={onLogout} />
      <header className="admin-header">
        <h1>Check-In Interface</h1>
        <p className="page-subtitle">Check in volunteers as they arrive on event day and track attendance in real time.</p>
      </header>

      <div className="checkin-content">
        <div className="checkin-controls">
          <div className="control-group">
            <label>Select Day:</label>
            <select value={selectedDay} onChange={(e) => setSelectedDay(e.target.value)}>
              {DAYS.map(day => (
                <option key={day} value={day}>{day}</option>
              ))}
            </select>
          </div>
          
          <div className="control-group">
            <label>Select Shift:</label>
            <select value={selectedShift} onChange={(e) => setSelectedShift(e.target.value)}>
              {shiftTimes.filter(s => !s.days || s.days.includes(selectedDay)).map(s => (
                <option key={s.name} value={s.name}>{s.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="checkin-stats">
          <div className="stat-card">
            <div className="stat-label">Total</div>
            <div className="stat-value">{stats.total}</div>
          </div>
          <div className="stat-card success">
            <div className="stat-label">Checked In</div>
            <div className="stat-value">{stats.checkedIn}</div>
          </div>
          <div className="stat-card warning">
            <div className="stat-label">Pending</div>
            <div className="stat-value">{stats.pending}</div>
          </div>
          <div className="stat-card danger">
            <div className="stat-label">No Shows</div>
            <div className="stat-value">{stats.noShows}</div>
          </div>
        </div>

        <div className="location-checkin-list">
          {locations.map(location => {
            const key = `${selectedDay}_${location}_${selectedShift}`;
            const assigned = (assignments[key] || []).filter(Boolean);
            
            if (assigned.length === 0) return null;

            const isExpanded = expandedLocations[location] !== false; // Default expanded

            return (
              <div key={location} className="location-checkin-section">
                <div 
                  className="location-checkin-header"
                  onClick={() => toggleLocation(location)}
                >
                  <span>{isExpanded ? '▼' : '▶'}</span>
                  <h3>📍 {location}</h3>
                  <span className="location-count">
                    {assigned.length} volunteer{assigned.length !== 1 ? 's' : ''}
                  </span>
                </div>

                {isExpanded && (
                  <div className="volunteer-checkin-list">
                    {assigned.map((volunteerId, index) => {
                      const volunteer = getVolunteerInfo(volunteerId);
                      if (!volunteer) return null;

                      const shiftId = key.replace(/\s+/g, '_');
                      const statusKey = `${volunteerId}_${shiftId}`;
                      const status = checkInStatus[statusKey] || 'pending';

                      return (
                        <div key={index} className={`volunteer-checkin-item status-${status}`}>
                          <div className="volunteer-info">
                            <div className="volunteer-name">
                              {volunteer.firstName} {volunteer.lastName}
                              {volunteer.volunteerType === 'smpw' && ' 🔵'}
                              {volunteer.isKeyman && ' 🔑'}
                            </div>
                            <div className="volunteer-details">
                              {volunteer.congregation || 'N/A'} • {volunteer.phone || 'No phone'}
                            </div>
                          </div>

                          <div className="checkin-actions">
                            <button
                              className={`btn-checkin ${status === 'checked-in' ? 'active' : ''}`}
                              onClick={() => handleCheckIn(volunteerId, shiftId, 'checked-in')}
                            >
                              ✓ Checked In
                            </button>
                            <button
                              className={`btn-pending ${status === 'pending' ? 'active' : ''}`}
                              onClick={() => handleCheckIn(volunteerId, shiftId, 'pending')}
                            >
                              ⏳ Pending
                            </button>
                            <button
                              className={`btn-noshow ${status === 'no-show' ? 'active' : ''}`}
                              onClick={() => handleCheckIn(volunteerId, shiftId, 'no-show')}
                            >
                              ✗ No Show
                            </button>
                          </div>

                          {volunteer.phone && (
                            <a href={`tel:${volunteer.phone}`} className="btn-call">
                              📞 Call
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default CheckInInterface;
